// Home page of software
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, IdCard, Users, MapPin, ChevronLeft, ChevronRight } from 'lucide-react';
import BASE_URL from './config'; // Import the base URL
import './index.css';  // Import the CSS file
import logo from './images/logo.png';
import { useToast, ToastContainer } from './Toast';
import AlertModal from './AlertModal';
// Removed carpool image - using text-focused design instead

function App() {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState('');
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  // A/B testing removed - using simple static login button

  const [showOnboardingModal, setShowOnboardingModal] = useState(false);
  const [newName, setNewName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newPhone, setNewPhone] = useState('');

  // Toast and Alert modal state
  const { toasts, showToast, removeToast } = useToast();
  const [alertModal, setAlertModal] = useState({ isOpen: false, title: '', message: '', type: 'info' });

  // Testimonials data
  const testimonials = [
    {
      quote: "Bulldog Carpool has made traveling so much easier and more affordable. I've met amazing people and saved hundreds of dollars on airport trips. The safety features give me peace of mind knowing I'm only riding with verified Yale students.",
      author: "Dhruv N.",
      class: "Class of 2026",
      initials: "DN"
    },
    {
      quote: "Such a convenient way to get to the airport! The app is super easy to use and I've made great friends through carpooling.",
      author: "Shyla A.",
      class: "Class of 2027",
      initials: "SA"
    },
    {
      quote: "Love how safe and reliable this platform is. Only Yale students can join, which makes me feel comfortable sharing rides.",
      author: "Alex M.",
      class: "Class of 2026",
      initials: "AM"
    },
    {
      quote: "Perfect for weekend trips! I've saved so much money and met awesome people. Highly recommend to all Yale students.",
      author: "Henry A.",
      class: "Class of 2026",
      initials: "HA"
    }
  ];

  // Auto-rotate testimonials
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [testimonials.length]);

  // Scroll to top of current page
  function goToHome() {
    // Just scroll to top - no navigation
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  // Smooth scroll to section
  function scrollToSection(sectionId) {
    // If not on homepage, navigate there first
    if (window.location.pathname !== '/') {
      navigate('/');
      // Wait for navigation to complete, then scroll
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ 
            behavior: 'smooth',
            block: 'start'
          });
        }
      }, 100);
    } else {
      // Already on homepage, just scroll
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ 
          behavior: 'smooth',
          block: 'start'
        });
      }
    }
  }

  // Navigate to rides page for the Rides button and only allows access if the user is signed in
  function goToRides() {
    if (isLoggedIn) {
      navigate('/rides');
    } else {
      setAlertModal({
        isOpen: true,
        title: 'Sign In Required',
        message: 'You need to sign in first to access the rides page.',
        type: 'warning'
      });
    }
  }

  // Navigates users to Yale's CAS system to sign in
  function handleLogin() {
    window.location.href = `${BASE_URL}/login`;
  }

  // Signs the current user out as long as there is someone already signed in to log out
  function handleLogout() {
    if (isLoggedIn) {
      // Redirect to backend logout endpoint which will handle the logout and redirect to homepage
      window.location.href = `${BASE_URL}/logout`;
    } else {
      setAlertModal({
        isOpen: true,
        title: 'No User Signed In',
        message: 'There is no user currently signed in.',
        type: 'info'
      });
    }
  }

  // Show error toast if CAS redirected back with a login_error param
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const loginError = params.get('login_error');
    if (loginError) {
      const messages = {
        timeout: 'Yale login timed out. Please try again.',
        cas_failure: 'Yale login failed. Please try again.',
        cas_unavailable: 'Yale login service is temporarily unavailable. Please try again in a moment.',
      };
      showToast(messages[loginError] || 'Login failed. Please try again.', 'error');
      window.history.replaceState({}, '', '/');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Check if someone is logged in and get their username
  useEffect(() => {
    if (window.location.pathname === '/') {
      fetch(`${BASE_URL}/check_login`, {
        method: 'GET',
        credentials: 'include'
      })
      .then(response => response.json())
      .then(data => {
        if (data.isLoggedIn) {
          setIsLoggedIn(true);
          setUser(data.user);
          navigate('/rides');
        } else {
          setIsLoggedIn(false);
          setShowOnboardingModal(false);
        }
      });
    }
  }, [navigate]);

  const handleUpdateUserInfo = (e) => {
    e.preventDefault();
    
    // Validate name - not empty, not just whitespace, reasonable length
    const trimmedName = newName.trim();
    if (!trimmedName) {
      setAlertModal({
        isOpen: true,
        title: 'Name Required',
        message: 'Please enter your full name.',
        type: 'error'
      });
      return;
    }
    
    if (trimmedName.length < 2) {
      setAlertModal({
        isOpen: true,
        title: 'Invalid Name',
        message: 'Please enter a valid full name (at least 2 characters).',
        type: 'error'
      });
      return;
    }
    
    if (trimmedName.length > 100) {
      setAlertModal({
        isOpen: true,
        title: 'Name Too Long',
        message: 'Please enter a name that is 100 characters or less.',
        type: 'error'
      });
      return;
    }
    
    // Validate email - not empty, valid format, and Yale domain
    const trimmedEmail = newEmail.trim();
    if (!trimmedEmail) {
      setAlertModal({
        isOpen: true,
        title: 'Email Required',
        message: 'Please enter your Yale email address.',
        type: 'error'
      });
      return;
    }
    
    // Basic email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmedEmail)) {
      setAlertModal({
        isOpen: true,
        title: 'Invalid Email Format',
        message: 'Please enter a valid email address.',
        type: 'error'
      });
      return;
    }
    
    // Validate Yale email domain
    if (!trimmedEmail.toLowerCase().endsWith('@yale.edu')) {
      setAlertModal({
        isOpen: true,
        title: 'Yale Email Required',
        message: 'Please enter a valid Yale email address ending with @yale.edu.',
        type: 'error'
      });
      return;
    }
    
    // Validate phone format - improved validation
    const trimmedPhone = newPhone.trim();
    if (!trimmedPhone) {
      setAlertModal({
        isOpen: true,
        title: 'Phone Number Required',
        message: 'Please enter your phone number.',
        type: 'error'
      });
      return;
    }
    
    // Remove common phone formatting characters and check for valid digits
    const phoneDigits = trimmedPhone.replace(/[\s\-()+]/g, '');
    if (phoneDigits.length < 10 || phoneDigits.length > 15) {
      setAlertModal({
        isOpen: true,
        title: 'Invalid Phone Number',
        message: 'Please enter a valid phone number with 10-15 digits.',
        type: 'error'
      });
      return;
    }
    
    // Check if phone contains only digits after removing formatting
    if (!/^\d+$/.test(phoneDigits)) {
      setAlertModal({
        isOpen: true,
        title: 'Invalid Phone Number',
        message: 'Phone number can only contain digits and common formatting characters (spaces, dashes, parentheses, plus sign).',
        type: 'error'
      });
      return;
    }
    
    fetch(`${BASE_URL}/update_user_info`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ username: user, name: trimmedName, email: trimmedEmail, phone: trimmedPhone })
    })
      .then(response => {
        // Check if response is ok before parsing JSON
        if (!response.ok) {
          return response.json().then(errorData => {
            throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
          }).catch(() => {
            throw new Error(`HTTP error! status: ${response.status}`);
          });
        }
        return response.json();
      })
      .then(data => {
        showToast('User info updated successfully!', 'success');
        setShowOnboardingModal(false);
        setNewName('');
        setNewEmail('');
        setNewPhone('');
        // Refresh login status to update profileComplete flag
        return fetch(`${BASE_URL}/check_login`, {
          method: 'GET',
          credentials: 'include'
        });
      })
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then(data => {
        // Redirect to rides after profile completion
        navigate('/rides');
      })
      .catch(error => {
        setAlertModal({
          isOpen: true,
          title: 'Submission Failed',
          message: `Failed to update user info: ${error.message}. Please try again.`,
          type: 'error'
        });
      });
  };

  return (
    <div className="app-container">
      <a href="#main-content" className="skip-link">Skip to main content</a>
      <header className="header">
        <div className="logo-container" onClick={goToHome}>
          <div className="logo-wrapper logo-light-mode">
            <img 
              src={logo} 
              alt="Logo" 
              className="logo"
            />
          </div>
          <h1>Bulldog Carpool</h1>
        </div>
        
        {/* Desktop Navigation */}
        <nav className="nav">
          <ul className="nav-links">
            <li><button className="nav-link" onClick={() => scrollToSection('how-it-works')}>How it works</button></li>
            <li><button className="nav-link" onClick={() => scrollToSection('see-it-in-action')}>See it in action</button></li>
            <li><button className="nav-link" onClick={() => scrollToSection('testimonials')}>Testimonials</button></li>
          </ul>
          
          <div className="nav-buttons">
            {isLoggedIn && (
              <button className="nav-button" onClick={handleLogout}>
                <LogOut className="nav-icon w-4 h-4" stroke="#00356B" strokeWidth={1.5} />
                Log Out
              </button>
            )}
          </div>
        </nav>

      </header>


      <main id="main-content">
        {/* Hero Section */}
        <section id="home" className="hero-section" style={{backgroundColor: 'var(--brand-50)'}}>
          <div className="container">
            <div className="hero-content hero-centered">
              <p className="hero-badge">Yale-verified · Students only</p>
              <h1>Split airport rides<br />with Yale students</h1>
              <p className="hero-subtitle">
                Coordinate shared rides to airports and train stations with verified Yalies.
                Save money, travel safer, and meet people going the same way.
              </p>
              <div className="hero-actions">
                <button className="btn-primary hero-btn" onClick={handleLogin}>
                  Login with Yale CAS
                </button>
              </div>
              <p className="hero-note">
                Yale NetID required · Student-only access
              </p>
            </div>
          </div>
        </section>

        {/* How it Works Section */}
        <section id="how-it-works" className="section">
          <div className="container">
            <div className="section-header">
              <h2>How it works</h2>
              <p>Get started with carpooling in three simple steps</p>
            </div>
            <div className="how-it-works-grid">
              <div className="how-it-works-card">
                <div className="card-icon">
                  <IdCard size={32} />
                </div>
                <h3>Log in</h3>
                <p>NetID verified access ensures only Yale students can participate</p>
              </div>
              <div className="how-it-works-card">
                <div className="card-icon">
                  <MapPin size={32} />
                </div>
                <h3>Find or post a ride</h3>
                <p>Filters by date, time, and destination help you find the perfect match.</p>
              </div>
              <div className="how-it-works-card">
                <div className="card-icon">
                  <Users size={32} />
                </div>
                <h3>Coordinate & go</h3>
                <p>Contact the driver, confirm seats, and enjoy your ride</p>
              </div>
            </div>
          </div>
        </section>

        {/* See it in Action Section */}
        <section id="see-it-in-action" className="section screenshots-section" style={{backgroundColor: 'var(--brand-50)'}}>
          <div className="container">
            <div className="section-header">
              <h2>See it in action</h2>
              <p>Experience the simplicity of finding and sharing rides</p>
            </div>
            <div className="screenshots-grid">
              <div className="screenshot-card">
                <div className="device-frame">
                  <img src="/feed.png" alt="Browse verified rides" className="screenshot-image" />
                </div>
                <h3>Browse verified rides</h3>
                <p>View available rides with real-time updates and verified groups</p>
              </div>
              <div className="screenshot-card">
                <div className="device-frame">
                  <img src="/add_ride.png" alt="Post your ride" className="screenshot-image" />
                </div>
                <h3>Post your ride in seconds</h3>
                <p>Create a new ride listing with just a few taps and start sharing</p>
              </div>
              <div className="screenshot-card">
                <div className="device-frame">
                  <img src="/my_rides.png" alt="Manage your rides" className="screenshot-image" />
                </div>
                <h3>Manage your rides</h3>
                <p>See who's joining your ride and connect with fellow Yale students</p>
              </div>
              <div className="screenshot-card">
                <div className="device-frame">
                  <img src="/messages.png" alt="Group chat" className="screenshot-image" />
                </div>
                <h3>Chat with your group</h3>
                <p>Coordinate pickup details and logistics with your ride participants</p>
              </div>
            </div>
          </div>
        </section>


        {/* Testimonials Section */}
        <section id="testimonials" className="section">
          <div className="container">
            <div className="section-header">
              <h2>What students are saying</h2>
            </div>
            <div className="testimonials-container">
              <button 
                className="testimonial-arrow testimonial-arrow-left"
                onClick={() => setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length)}
                aria-label="Previous testimonial"
              >
                <ChevronLeft size={24} />
              </button>
              
              <div className="testimonial-slider">
                {testimonials.map((testimonial, index) => (
                  <div 
                    key={index}
                    className={`testimonial-slide ${index === currentTestimonial ? 'active' : ''}`}
                  >
                    <blockquote className="testimonial-quote">
                      "{testimonial.quote}"
                    </blockquote>
                    <div className="testimonial-author">
                      <div className="author-avatar">
                        <span>{testimonial.initials}</span>
                      </div>
                      <div className="author-info">
                        <div className="author-name">{testimonial.author}</div>
                        <div className="author-title">Yale Student, {testimonial.class}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              <button 
                className="testimonial-arrow testimonial-arrow-right"
                onClick={() => setCurrentTestimonial((prev) => (prev + 1) % testimonials.length)}
                aria-label="Next testimonial"
              >
                <ChevronRight size={24} />
              </button>
              
              <div className="testimonial-dots">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    className={`testimonial-dot ${index === currentTestimonial ? 'active' : ''}`}
                    onClick={() => setCurrentTestimonial(index)}
                    aria-label={`Go to testimonial ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </section>

      </main>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-brand">
              <div className="footer-logo">
                <img src={logo} alt="Bulldog Carpool Logo" className="footer-logo-img" />
                <span className="footer-logo-text">Bulldog Carpool</span>
              </div>
              <p className="footer-mission">
                Connecting Yale students for safe, convenient, and affordable transportation.
              </p>
            </div>
            
            <div className="footer-contact">
              <h4>Contact</h4>
              <p>Questions or feedback?</p>
              <a href="mailto:owen.zhang@yale.edu" className="footer-email">
                owen.zhang@yale.edu
              </a>
            </div>
          </div>
          
          <div className="footer-bottom">
            <p>&copy; {new Date().getFullYear()} Bulldog Carpool. Built for Yale students, by Yale students.</p>
          </div>
        </div>
      </footer>

      {/* Onboarding Modal for New Users */}
      {showOnboardingModal && (
        <div className="modal-overlay onboarding-modal">
          <div className="onboarding-container">
            <div className="onboarding-header">
              <h2>Welcome to Bulldog Carpool!</h2>
              <p>Let's get you set up with your profile</p>
            </div>
            <form onSubmit={handleUpdateUserInfo}>
              <div className="form-group">
                <label htmlFor="onboard-name">Full Name</label>
                <input 
                  id="onboard-name"
                  type="text" 
                  value={newName} 
                  onChange={(e) => setNewName(e.target.value)} 
                  placeholder="Enter your full name"
                  required 
                />
              </div>
              <div className="form-group">
                <label htmlFor="onboard-email">Yale Email</label>
                <input 
                  id="onboard-email"
                  type="email" 
                  value={newEmail} 
                  onChange={(e) => setNewEmail(e.target.value)} 
                  placeholder="your.email@yale.edu"
                  required 
                />
              </div>
              <div className="form-group">
                <label htmlFor="onboard-phone">Phone Number</label>
                <input 
                  id="onboard-phone"
                  type="tel" 
                  value={newPhone} 
                  onChange={(e) => setNewPhone(e.target.value)} 
                  placeholder="+1 (203) 555-1234"
                  required 
                />
                <small>This will be shared with riders to coordinate pickups</small>
              </div>
              <button type="submit" className="cta-button">Complete Setup</button>
            </form>
          </div>
        </div>
      )}

      {/* Toast notifications */}
      <ToastContainer toasts={toasts} removeToast={removeToast} />

      {/* Alert modal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
        title={alertModal.title}
        message={alertModal.message}
        type={alertModal.type}
      />
    </div>
  );
}

export default App;
